import { ReactComponent as IconErrorHint } from "@/assets/svgs/error-hint.svg";
import useEForm from "@/hooks/use-eform/index";
import addFormats from "@/utils/custom-ajv-formats";
import Ajv from "ajv";
import classname from 'classnames';
import PropTypes from 'prop-types';
import { Fragment, useState } from "react";
import CellBooleanRadioComponent from "./FormField/cell-boolean-radio";
import CellDropdownComponent from "./FormField/cell-dropdown";
import CellFileUploadComponent from "./FormField/cell-file-upload";
import CellMultiDropdownComponent from "./FormField/cell-multi-select";
import CellNumberComponent from "./FormField/cell-number";
import CellStringComponent from "./FormField/cell-string";

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

export const parentPropsToChangedField = (parentProps) => {
	// parentProps = ['a', 1, 'b'] => 'a[1].b'
	return parentProps.reduce((acc, curr, index) => {
		if (index === 0) {
			return curr;
		}
		if (typeof curr === 'number') {
			return `${acc}[${curr}]`;
		}
		return `${acc}.${curr}`;
	}, '');
}

export const extractPreviousValueFromPreviousPayload = (previousPayload, parentProps) => {
	if (parentProps.length === 1) {
		return previousPayload?.[parentProps[0]] ?? null;
	}
	if (parentProps.length === 0) {
		return '';
	}
	const nextPayload = previousPayload?.[parentProps[0]];
	if (nextPayload == null) {
		return null;
	}
	return extractPreviousValueFromPreviousPayload(nextPayload, parentProps.slice(1));
}

const EFormFieldComponent = ({
	payload,
	schema,
	parentProps = [],
	required,
	isReadOnly,
	setPayload,
	setIsValidFormat,
	previousPayload,
	changedFields,
}) => {
	const [inputProps, setInputProps] = useState({});

	const { getFieldValue, getFullJson, getNextPayload } = useEForm();
	const currentPayload = getFieldValue(payload, parentProps);

	const schemaType = schema?.["x-uitype"] ? schema?.["x-uitype"] : schema?.type;
	const currentType = Array.isArray(schemaType)
		? schemaType.find((type) => type !== "null")
		: schemaType;

	const propertyName = parentProps[parentProps.length - 1];
	const previousValue = previousPayload && changedFields?.includes(parentPropsToChangedField(parentProps)) ?
		(extractPreviousValueFromPreviousPayload(previousPayload, parentProps) ?? '') :
		null;
	const [eFormFieldError, setEFormFieldError] = useState(false);

	const fieldProps = {
		isReadOnly,
		schema,
		payload,
		parentProps,
		inputProps,
		setInputProps,
		getFieldValue,
		updatePayload,
		required,
		propertyName,
		validateSchema,
		setIsValidFormat,
		error: eFormFieldError,
		setEFormFieldError,
		previousPayload
	}

	function updatePayload(value) {
		const nextPayload = getNextPayload(payload, parentProps, value);
		setPayload(nextPayload);
		return nextPayload;
	}

	function validateSchema(value) {
		const validate = ajv.compile(schema);
		const validateResult = validate(value);
		setEFormFieldError(!validateResult);
		return validateResult;
	}

	const cellClassName = classname(
		{
			"eform-read-mode__field-value": isReadOnly,
			"eform-read-mode__field-value--changed": isReadOnly && previousValue !== null,
		}
	)

	switch (currentType) {
		case "object":
			return (
				<>
					{Object.keys(currentPayload?? {})?.map((key, index) => {
						if (key === "attachments") {
							return <></>;
						}

						return (
							<EFormFieldComponent
								key={key}
								isReadOnly={isReadOnly}
								schema={schema?.properties[key]}
								required={schema?.required}
								payload={payload}
								setPayload={setPayload}
								parentProps={[...parentProps, key]}
								setIsValidFormat={setIsValidFormat}
								previousPayload={previousPayload}
								changedFields={changedFields}
							/>

						);
					})}
				</>
			);

		case "array":
			return (
				<div className="eform-read-mode__array-panel" data-testid="eform-read-mode-array-panel">
					<div className="eform-read-mode__array-panel-title">
						{required?.includes(propertyName) && (
							<span className="eform-read-mode-label__asterisk" data-testid="array-panel-label-asterisk">*</span>
						)}
						<span data-testid="array-panel-label">{schema?.title}</span>
					</div>
					{currentPayload?.map((_payload, arrIdx) => (
						<Fragment key={`${schema?.description}_${arrIdx}`}>
							<div className="eform-read-mode__array-top-banner mb-1 flex-box flex-align-center flex-space-between font-size-m font-weight-bold" data-testid="array-panel-item-title">
								{`Item #${arrIdx + 1}`}
							</div>

							<div className="eform-read-mode__form mb-2">
								<EFormFieldComponent
									isReadOnly={isReadOnly}
									schema={schema?.items}
									required={schema?.required}
									payload={payload}
									setPayload={setPayload}
									parentProps={[...parentProps, arrIdx]}
									setIsValidFormat={setIsValidFormat}
									previousPayload={previousPayload}
									changedFields={changedFields}
								/>
							</div>
						</Fragment>
					))}

				</div>
			);

		default:
			return (
				<div className="eform-read-mode__data-field" data-testid="eform-read-mode-data-field">
					<div className="eform-read-mode__label tooltip">
						{required?.includes(propertyName) && (
							<span
								className="eform-read-mode-label__asterisk"
								data-testid={`${propertyName}-label-asterisk`}
							>
								*
							</span>
						)}

						<div className="eform-read-mode__label" data-testid={`${propertyName}-label`}>
							{schema?.title ?? schema?.description}
						</div>

						<div className="tooltiptext eform-tooltip-text" data-testid={`${propertyName}-tooltip-text`}>
							{schema?.title ?? schema?.description}
						</div>
					</div>
					<div className={cellClassName} data-testid={`field-value-${propertyName}`}>
						{currentType === "string" && <CellStringComponent {...fieldProps} />}
						{currentType === "dropdown" && <CellDropdownComponent {...fieldProps} />}
						{currentType === "multi-select" && <CellMultiDropdownComponent {...fieldProps} />}
						{currentType === "file-upload" && <CellFileUploadComponent {...fieldProps} />}
						{(currentType === "number" || currentType === "integer") && <CellNumberComponent {...fieldProps} />}
						{(currentType === "radio" || currentType === "boolean") && <CellBooleanRadioComponent {...fieldProps} />}
					</div>

					{!isReadOnly && eFormFieldError && (
						<div className="eform-input-error flex-box flex-align-center">
							<IconErrorHint />
							{<div>{schema.title ?? schema.description} is not valid</div>}
						</div>
					)}
					{isReadOnly && previousValue !== null && (
						<div className="eform-read-mode__previous-value">
							Previous value: {previousValue}
						</div>
					)}
				</div>
			);
	}
}

const EFormReadComponent = ({
	payload,
	schema,
	isReadOnly = true,
	setPayload,
	setIsValidFormat,
	previousPayload,
	changedFields,
}) => {

	return (
		<div className="eform-read-mode__form">
			<EFormFieldComponent
				payload={payload}
				schema={schema}
				isReadOnly={isReadOnly}
				setPayload={setPayload}
				setIsValidFormat={setIsValidFormat}
				previousPayload={previousPayload}
				changedFields={changedFields}
			/>
		</div>
	)
}

EFormFieldComponent.propTypes = {
	payload: PropTypes.object,
	parentProps: PropTypes.array,
	required: PropTypes.array,
	schema: PropTypes.shape({
		type: PropTypes.string,
		properties: PropTypes.object,
		required: PropTypes.array,
		'x-uitype': PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.arrayOf(PropTypes.string),
		]),
		title: PropTypes.string,
		description: PropTypes.string,
		items: PropTypes.object,
	}),
	isReadOnly: PropTypes.bool,
	setPayload: PropTypes.object,
	setIsValidFormat: PropTypes.any,
	previousPayload: PropTypes.object,
	changedFields: PropTypes.array,
};

EFormReadComponent.propTypes = {
	payload: PropTypes.object,
	schema: PropTypes.object,
	isReadOnly: PropTypes.bool,
	setPayload: PropTypes.object,
	setIsValidFormat: PropTypes.any,
	previousPayload: PropTypes.object,
	changedFields: PropTypes.array,
};

export default EFormReadComponent;
